import { Col } from '@amzn/stencil-react-components/layout';
import { Select, SelectLayout } from '@amzn/stencil-react-components/select';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';

const SEARCHABLE_THRESHOLD = 10;

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

const renderLabel = (opt: unknown): string => (opt as SelectOption).label;

export interface FormSelectFieldProps {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly hasError?: boolean;
  readonly dataTestId?: string;
}

/**
 * Reusable form select field with RequiredLabel + Stencil Select.
 * Used in CreateAuditSheet to reduce repetition across location,
 * division, template, and audit type fields.
 */
export function FormSelectField({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  hasError = false,
  dataTestId,
}: FormSelectFieldProps): JSX.Element {
  const selected = value ? options.find((o) => o.value === value) : undefined;
  return (
    <Col gridGap="dimensions.spacing.100">
      <RequiredLabel isRequired hasError={hasError}>
        {label}
      </RequiredLabel>
      <Select
        options={options as SelectOption[]}
        layout={SelectLayout.Custom}
        onChange={(opt) => {
          const picked = opt as SelectOption | null | undefined;
          onChange(picked?.value ?? '');
        }}
        valueAccessor="value"
        renderOption={renderLabel}
        renderSelectedValue={renderLabel}
        isSearchable={options.length >= SEARCHABLE_THRESHOLD}
        placeholder={placeholder}
        disabled={disabled}
        error={hasError}
        aria-label={label}
        {...(selected ? { value: selected } : {})}
        data-testid={dataTestId}
      />
    </Col>
  );
}
